import {
  AdminEmail,
  AdminEmailsResponse,
  AdminEmailSubject,
  AdminEmailHtml,
  AdminEmailTemplateHtml,
} from '@platform/client/schemas';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { formatDateStr } from '../../dashboard/my-projects/utils';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../components/ProfileComponents';
import { AdminTable } from '../AdminTable';
import { Modal } from '@platform/components';
import { EmailPreview } from '../components/EmailPreview';
import { IntroductionLink } from '../pages/introductions/IntroductionsAdmin';
import { TeamMatchLink } from '../pages/teams-matching/TeamsMatchAdmin';

type EmailTabContentProps = {
  data?: AdminEmailsResponse;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  recordType: 'professional' | 'organization' | 'matching';
};

const matchColumnHelper = createColumnHelper<AdminEmail>();

export const EmailTabSection = ({
  data,
  isLoading,
  isError,
  isSuccess,
  recordType,
}: EmailTabContentProps) => {
  const [previewEmail, setPreviewEmail] = useState<{
    subject: AdminEmailSubject;
    html: AdminEmailHtml | AdminEmailTemplateHtml;
  } | null>(null);
  const columns = useMemo(
    () =>
      [
        matchColumnHelper.accessor('emailStatus', {
          header: 'Status',
          cell: (info) => info.getValue(),
          size: 5,
        }),
        matchColumnHelper.accessor('emailType', {
          header: 'Template',
          cell: ({ row }) =>
            row.original.templateHtml ?
              <button
                className='text-brand-500 hover:underline text-left'
                onClick={() =>
                  setPreviewEmail({
                    subject: 'Preview Template',
                    html: row.original.templateHtml?.toString() as AdminEmailTemplateHtml,
                  })
                }
              >
                {row.original.emailType.replaceAll('_', ' ')}
              </button>
            : row.original.emailType.replaceAll('_', ' '),
          size: 150,
        }),
        ...(recordType !== 'matching' ?
          [
            matchColumnHelper.accessor('introductionId', {
              header: 'Match',
              cell: ({ row }) => (
                <EmailMatchLink
                  adminEmail={row.original}
                  includeOrganization={recordType === 'professional'}
                />
              ),
              size: 5,
            }),
          ]
        : []),
        ...(recordType !== 'professional' ?
          [
            matchColumnHelper.accessor('toUserFirstName', {
              header: 'Recipient',
              cell: ({ row }) => `${row.original.toUserFirstName} ${row.original.toUserLastName}`,
              size: 5,
            }),
          ]
        : []),
        matchColumnHelper.accessor('scheduledFor', {
          header: 'Date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 1,
        }),
        matchColumnHelper.display({
          id: 'preview',
          cell: ({ row }) =>
            row.original.html ?
              <button
                className='text-blue-500 underline'
                onClick={() =>
                  setPreviewEmail({
                    subject: row.original.subject,
                    html: row.original.html as AdminEmailHtml,
                  })
                }
              >
                <p className='blue-link text-xs'>see content</p>
              </button>
            : 'no content available',
          size: 1,
        }),
      ] as ColumnDef<AdminEmail>[],
    [recordType],
  );
  return (
    <ProfileTabContainer>
      {isSuccess && data?.emails.length === 0 ?
        <NoRecordsFoundTabContent recordType={recordType} />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={data?.emails || []}
          isSimple
          filteredCount={data?.emails.length ?? 0}
        />
      }
      <Modal
        title={previewEmail?.subject ?? 'Email Preview'}
        variant='full-screen'
        headerDivision='divider'
        isOpen={!!previewEmail}
        onClose={() => setPreviewEmail(null)}
        ignoreExtraPadding
      >
        <EmailPreview html={previewEmail?.html as AdminEmailHtml | AdminEmailTemplateHtml} />
      </Modal>
    </ProfileTabContainer>
  );
};

const EmailMatchLink = ({
  adminEmail,
  includeOrganization = false,
}: {
  adminEmail: AdminEmail;
  includeOrganization?: boolean;
}) => {
  if (!adminEmail.introductionId && !adminEmail.teamAssignmentDiscoveryId) {
    return;
  }
  let linkText =
    adminEmail.introductionId ?
      `${adminEmail.introductionId}`
    : `${adminEmail.teamAssignmentDiscoveryId}`;
  linkText +=
    includeOrganization && adminEmail.matchOrganizationName ?
      ` [${adminEmail.matchOrganizationName}]`
    : '';
  if (adminEmail.introductionId) {
    return (
      <IntroductionLink introductionId={adminEmail.introductionId} introductionName={linkText} />
    );
  } else {
    return (
      <TeamMatchLink
        discoveryId={adminEmail.teamAssignmentDiscoveryId as number}
        discoveryName={linkText}
      />
    );
  }
};
